import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { SettingsBox } from 'src/Elements/Data'
import { NumberField } from 'src/Libs/inputs/formik'
import WallboxFormContent from '../WallboxFormContent'
import BatteryFormContent from '../BatteryFormContent'
import ActiveContractCard from '../ActiveContractCard'
import ExtensionsImage from './ExtensionsImage'
import { J2Validate } from '@e3dc-react/icons'
import { Paper } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import useInstallationExtensionData from 'src/Libs/installation-extensions/hooks/extensions/use-installation-extension-data'
import { UnitTypes } from 'src/Libs/types'
import { useFetchDynamicTariffSettings } from 'src/Libs/dynamic-tariff-settings/hooks'
import { checkIsEnergyCostDynamic } from 'src/Libs/dynamic-power-prices/utils'
import { PriceBasedChargingAlert } from '../../alert'

const useStyles = makeStyles()(theme => ({
  connectedStatus: {
    border: `1px solid ${theme.palette.grey.grey06}`,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
    gap: theme.spacing(1),
  },
}))

interface Props {
  installationId?: string
  isDppWallboxToggleDisabled: boolean
  hasWritingRights?: boolean
}

/**
 * This form includes all dynamic prices fields
 */
const FullVersionFormContent: React.FC<Props> = ({ installationId, isDppWallboxToggleDisabled, hasWritingRights = true }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const { installationExtensionsState, isActiveExtensionAvailable } = useInstallationExtensionData(installationId)

  const { dynamicTariffSettings } = useFetchDynamicTariffSettings(installationId)

  /* The price based charging is only available if the energy costs are dynamic */
  const isEnergyCostDynamic = checkIsEnergyCostDynamic(dynamicTariffSettings)

  const canUserChangeSettings = hasWritingRights && (isEnergyCostDynamic || isActiveExtensionAvailable)
  const showPriceBasedChargingAlert = !isEnergyCostDynamic && !isActiveExtensionAvailable

  return (
    <>
      {showPriceBasedChargingAlert && (
        <PriceBasedChargingAlert text={t('systemSettings.price-based-charging.alert.electricity-tariff-not-dynamic')} />
      )}
      {isActiveExtensionAvailable && (
        <ActiveContractCard
          image={<ExtensionsImage installationExtensionsState={installationExtensionsState} />}
          suffix={
            <Paper className={classes.connectedStatus} elevation={0}>
              {t('installation-extensions.ostrom.connected')}
              <J2Validate color="primary" />
            </Paper>
          }
        />
      )}
      <WallboxFormContent isDppWallboxToggleDisabled={isDppWallboxToggleDisabled} hasWritingRights={canUserChangeSettings} />
      <BatteryFormContent hasWritingRights={canUserChangeSettings} />
      <SettingsBox
        title="systemSettings-dynamic-prices.reference-price.title"
        description="systemSettings-dynamic-prices.reference-price.description"
        suffix={
          <NumberField
            name={'referencePrice'}
            suffix={UnitTypes.centsPerKiloWattHour}
            isDecimal={true}
            step={0.01}
            precision={2}
            disabled={!canUserChangeSettings}
          />
        }
      />
    </>
  )
}

export default FullVersionFormContent
